import React, { useEffect, useRef, useState } from "react";
import { fetchInvitationsAPI } from "@/services/api/invitation";
import SearchBox from "@/components/SearchBox/SearchBox";
import styles from "./ManagerEmailDropdown.module.scss";

interface InvitationItem {
  id: string | number;
  email: string;
  name?: string;
}

interface Props {
  value: string; // currently selected email
  onChange: (email: string, id: string | number) => void;
}

const LIMIT = 10;
const DEBOUNCE_MS = 350;

const ManagerEmailDropdown: React.FC<Props> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<InvitationItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const searchRef = useRef("");

  pageRef.current = page;
  hasMoreRef.current = hasMore;
  searchRef.current = search;

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset search when closed
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // Fetch page of invitations
  const fetchPage = async (pageNo: number, term: string, reset: boolean) => {
    setLoading(true);
    try {
      const res = await fetchInvitationsAPI({
        search: term || null,
        page: pageNo,
        limit: LIMIT,
      });
      const data = res?.data ?? res;
      const raw: any[] = data?.results || data?.rows || (Array.isArray(data) ? data : []);
      
      // Only keep accepted managers and owners
      const filtered = raw.filter((r) => {
        const role = (r.role || "").toLowerCase();
        const accepted = !!r.name;
        return (role === "manager" || role === "owner") && accepted;
      });

      const results: InvitationItem[] = filtered.map((r) => ({
        id: r.id,
        email: r.email || "",
        name: r.name || "",
      }));

      setItems((prev) => {
        return reset ? results : [...prev, ...results];
      });
      setHasMore(raw.length === LIMIT);
      setPage(pageNo);
    } catch (error) {
      console.error("Failed to fetch invitations", error);
    } finally {
      setLoading(false);
    }
  };

  // Load first page on open
  useEffect(() => {
    if (open) {
      fetchPage(1, search, true);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setItems([]);
      setPage(1);
      setHasMore(true);
      fetchPage(1, search, true);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search]);

  // Infinite scroll
  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || !hasMoreRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      fetchPage(pageRef.current + 1, searchRef.current, false);
    }
  };

  const handleSelect = (item: InvitationItem) => {
    onChange(item.email, item.id);
    setOpen(false);
  };

  // Resolve ID for the initial value (if it exists and is not yet in the items list)
  useEffect(() => {
    if (!value) return;

    // Check if the current value is already in the items list
    const found = items.find((item) => item.email === value);
    if (found) {
      onChange(found.email, found.id);
      return;
    }

    // Otherwise, fetch it to find its matching invitation ID
    const resolveInitialId = async () => {
      try {
        const res = await fetchInvitationsAPI({
          search: value,
          page: 1,
          limit: 1,
        });
        const data = res?.data ?? res;
        const rows = data?.results || data?.rows || [];
        const match = rows.find((r: any) => r.email === value);
        if (match) {
          onChange(match.email, match.id);
        }
      } catch (err) {
        console.error("Failed to resolve initial email ID", err);
      }
    };
    resolveInitialId();
  }, [value]);

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={value ? styles.selectedText : styles.placeholder}>
          {value || "Select Manager Email"}
        </span>
        <span className={styles.triggerRight}>
          <svg
            className={`${styles.chevron} ${open ? styles.chevronUp : ""}`}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrap} onClick={(e) => e.stopPropagation()}>
            <SearchBox
              value={search}
              onChange={(val) => setSearch(val)}
              // placeholder="Search invitations..."
              width="100%"
            />
          </div>

          <div className={styles.divider} />

          <div className={styles.list} ref={listRef} onScroll={handleScroll}>
            {items.length === 0 && !loading && (
              <div className={styles.empty}>
                {search ? `No results for "${search}"` : "No invitations found"}
              </div>
            )}

            {items.map((item) => (
              <div
                key={item.id}
                className={`${styles.option} ${value === item.email ? styles.optionSelected : ""}`}
                onClick={() => handleSelect(item)}
              >
                <div className={styles.optionBody}>
                  <span className={styles.optionName}>{item.email}</span>
                  {item.name && <span className={styles.optionSubject}>{item.name}</span>}
                </div>
              </div>
            ))}

            {loading && (
              <div className={styles.loader}>
                <span className={styles.spinner} />
                {search ? "Searching..." : "Loading..."}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerEmailDropdown;
